import type {
  CompareColumn,
  CompareEditor,
  CompareField,
  CompareRow,
} from '~/components/common/enrichment-compare-grid/compare-grid.types';
import { computeDiffStatus } from '~/components/common/enrichment-diff-table/diff-status.util';
import type { ProductListItem } from '~/features/experiments/types/product-list.types';

import { ENRICHED_FIELD_LABELS } from '../components/enriched-field-registry';
import type { PipelineEnrichedProduct } from '../types/scheduled-enrichment.types';
import { getBeforeFieldValue } from '../utils/before-enrichment-field-mapper';

// ---------------------------------------------------------------------------
// Field-name helpers — shared with the table variants so both presentations
// label and key columns identically.
// ---------------------------------------------------------------------------

/** Converts a snake_case API field name to camelCase, the key shape used by the attribution list and the before-field mapper. */
export function snakeToCamel(name: string): string {
  return name.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/** Converts a camelCase attribute key back to the snake_case wire field the edit PATCH expects (e.g. `productType` → `product_type`, `productLength` → `product_length`). */
export function camelToSnake(key: string): string {
  return key.replace(/([A-Z0-9])/g, (c: string) => `_${c.toLowerCase()}`);
}

/** Converts a snake_case field_name to a human-readable label, for API fields absent from `ENRICHED_FIELD_LABELS`. */
export function formatFieldLabel(fieldName: string): string {
  return fieldName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Converts a camelCase key to a Title Case label (e.g. `productType` → `Product Type`). */
export function camelToLabel(key: string): string {
  return key
    .replace(/([A-Z0-9])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Derives a display label for a field absent from the registry. Call sites feed
 * this either snake_case wire names (scheduled `fields[]`) or camelCase
 * attribution keys, so the casing is detected rather than assumed.
 */
export function deriveFieldLabel(name: string): string {
  return name.includes('_') ? formatFieldLabel(name) : camelToLabel(name);
}

// ---------------------------------------------------------------------------
// Editor resolution
// ---------------------------------------------------------------------------

/**
 * Fields whose value is a structured shape (card lists, Q&A pairs, wrapped
 * content objects) rather than a scalar. They are still editable, but in their
 * raw JSON form — a single text input would flatten them lossily, so the viewer
 * serialises them to JSON for editing and parses them back on save.
 */
const STRUCTURED_FIELD_KEYS = new Set<string>([
  'structuredTitle',
  'structuredDescription',
  'productHighlight',
  'productDetail',
  'questionAndAnswer',
  'relatedProduct',
  'variantOption',
  'isBundle',
  'popularityRank',
]);

/** Long-form fields that need a multi-line editor rather than a single-line input. */
const TEXTAREA_FIELD_KEYS = new Set<string>(['description']);

/** Registry labels widened to an open lookup — the scheduled API can return field names the attribution list doesn't cover. */
const FIELD_LABELS: Record<string, string | undefined> = ENRICHED_FIELD_LABELS;

/** Picks the inline editor for a field: raw JSON for structured shapes, a
 *  multi-line box for long prose, a single-line input for everything else. */
export function resolveEditor(key: string): CompareEditor {
  if (STRUCTURED_FIELD_KEYS.has(key)) return 'json';
  if (TEXTAREA_FIELD_KEYS.has(key)) return 'textarea';
  return 'text';
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

/**
 * Builds compare-grid columns from a flat flow's visible-field list — already
 * filtered to keys that are non-null somewhere in the product set, so the column
 * order matches the table variant exactly. Used by the experiment pipeline, the
 * experiment sample preview, and the Meta sample preview, all of which expose
 * enriched values as camelCase properties on the product object.
 */
export function toCompareColumns(visibleFields: readonly string[]): CompareColumn[] {
  return visibleFields.map((key) => ({
    key,
    // This flow only exposes camelCase keys, so the wire name is derived back to snake_case.
    wireField: camelToSnake(key),
    label: FIELD_LABELS[key] ?? deriveFieldLabel(key),
    editor: resolveEditor(key),
  }));
}

/**
 * Builds compare-grid columns from the scheduled (Meta) flow's raw snake_case
 * `fields[]` names. Keys are camelCased so before-value lookups resolve, while
 * labels stay derived from the original API name.
 */
export function toCompareColumnsFromScheduled(fieldNames: readonly string[]): CompareColumn[] {
  return fieldNames.map((name) => {
    const key = snakeToCamel(name);
    return {
      key,
      // The scheduled API already carries the snake_case wire name — no derivation needed.
      wireField: name,
      label: FIELD_LABELS[key] ?? deriveFieldLabel(name),
      editor: resolveEditor(key),
    };
  });
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** Resolves the feed title as a display string — the before-mapper types every field as `unknown`, but `title` is always textual. */
function feedTitleOf(before: ProductListItem | undefined): string | undefined {
  const value = before ? getBeforeFieldValue(before, 'title') : undefined;
  return typeof value === 'string' ? value : undefined;
}

/** Looks up an operator's local edit for one field, or `undefined` when untouched.
 *  Returns the value in the attribute's own shape (a string for text fields, a
 *  parsed object/array for `json` fields) so the cell re-renders formatted. */
export type EditLookup = (productId: string, fieldKey: string) => unknown;

/**
 * Builds the per-column comparison map for one product, pairing each feed value
 * against its enriched counterpart.
 *
 * A local edit replaces the model output for both display and diffing, so an
 * operator who fixes a dropped value sees the cell stop reading as `removed`.
 */
function buildFields(
  columns: readonly CompareColumn[],
  before: ProductListItem | undefined,
  productId: string,
  getEnrichedValue: (key: string) => unknown,
  getEdit?: EditLookup,
): Record<string, CompareField> {
  const fields: Record<string, CompareField> = {};
  for (const column of columns) {
    const feedValue = before ? getBeforeFieldValue(before, column.key) : undefined;
    const modelRaw = getEnrichedValue(column.key);
    const edit = getEdit?.(productId, column.key);
    const enrichedValue = edit ?? modelRaw;
    fields[column.key] = {
      key: column.key,
      feedValue,
      enrichedValue,
      modelValue: typeof modelRaw === 'string' ? modelRaw : '',
      status: computeDiffStatus(feedValue, enrichedValue),
      isEdited: edit !== undefined,
    };
  }
  return fields;
}

/**
 * The shape shared by every endpoint that exposes enriched values as plain
 * camelCase properties on the product: the experiment pipeline, the experiment
 * sample preview, and the Meta sample preview. (The Meta *pipeline* endpoint
 * differs — it returns a `fields[]` array; see `toCompareRowsFromScheduled`.)
 */
export interface FlatEnrichedProduct {
  productId: string;
  /** Join key against the before-products list. Optional — the sample-preview payloads may omit it. */
  externalId?: string;
  title: string;
  imageLink?: string | null;
  /** Lily-processed image variants; preferred over `imageLink`, which is often empty on enriched rows. */
  optimizedImages?: string[] | null;
  score?: number | null;
}

interface FlatCompareOptions<T> {
  /**
   * Overrides the default failure rule (no enriched output across any visible
   * column). The sample-preview endpoints instead signal failure with a null
   * score, since `@JsonInclude(NON_NULL)` omits it entirely.
   */
  isFailed?: (product: T) => boolean;
  /** Supplies the operator's local edits so they override the model output for display and diffing. */
  getEdit?: EditLookup;
}

/**
 * Normalizes a flat-shaped enriched product list into compare rows.
 * `beforeById` is the same externalId → catalog-item map the table variant
 * builds, so no extra request is needed to switch presentations.
 */
export function toCompareRows<T extends FlatEnrichedProduct>(
  products: readonly T[],
  beforeById: Map<string, ProductListItem>,
  columns: readonly CompareColumn[],
  options: FlatCompareOptions<T> = {},
): CompareRow[] {
  return products.map((product) => {
    const before = product.externalId ? beforeById.get(product.externalId) : undefined;
    // Enriched values live under arbitrary attribute keys, so the product is read
    // as an open record — the typed interfaces carry no index signature.
    const values = product as unknown as Record<string, unknown>;

    return {
      productId: product.productId,
      externalId: product.externalId ?? '',
      title: product.title,
      feedTitle: feedTitleOf(before),
      imageUrl: product.optimizedImages?.[0] ?? product.imageLink ?? null,
      score: product.score ?? null,
      generationFailed: options.isFailed
        ? options.isFailed(product)
        : // Mirrors the table variant: no enriched output across any visible column means generation failed.
          columns.length > 0 && columns.every((c) => values[c.key] == null),
      fields: buildFields(
        columns,
        before,
        product.productId,
        (key) => values[key] ?? null,
        options.getEdit,
      ),
    };
  });
}

/** Extracts a numeric score from the scheduled product's `scores` field, which is either a number or an `{ overall }` object. */
function extractProductScore(scores: PipelineEnrichedProduct['scores']): number | null {
  if (scores == null) return null;
  if (typeof scores === 'number') return scores;
  if (typeof scores === 'object' && 'overall' in scores) return scores.overall ?? null;
  return null;
}

/**
 * Normalizes scheduled (Meta) enriched products into compare rows.
 * Values come out of the `fields[]` array, preferring the richest available
 * representation (`objectValue` > `values` > `value`) so structured renderers
 * receive the correct JS type — same precedence as the table variant.
 */
export function toCompareRowsFromScheduled(
  products: readonly PipelineEnrichedProduct[],
  beforeById: Map<string, ProductListItem>,
  columns: readonly CompareColumn[],
  options: { getEdit?: EditLookup } = {},
): CompareRow[] {
  return products.map((product) => {
    const before = beforeById.get(product.externalId);
    const valueByKey = new Map<string, unknown>(
      product.fields.map((f) => [
        snakeToCamel(f.name),
        f.objectValue ?? (f.values?.length ? f.values : null) ?? f.value ?? null,
      ]),
    );

    const rawTitle = valueByKey.get('title');

    return {
      productId: product.productId,
      externalId: product.externalId,
      title: typeof rawTitle === 'string' ? rawTitle : product.externalId,
      feedTitle: feedTitleOf(before),
      imageUrl: product.optimizedImages?.[0] ?? product.imageLink ?? null,
      score: product.score ?? extractProductScore(product.scores),
      // Mirrors the table variant: the scheduled API reports failure explicitly,
      // and a missing/zero score means nothing usable came back.
      generationFailed: !product.success || !product.score,
      fields: buildFields(
        columns,
        before,
        product.productId,
        (key) => valueByKey.get(key) ?? null,
        options.getEdit,
      ),
    };
  });
}
